import {
  CascadedShadowGenerator,
  Color3,
  Mesh,
  Quaternion,
  Scene,
  StandardMaterial,
  TransformNode,
  Vector3,
} from '@babylonjs/core';
import { RAPIER, type PhysicsWorld } from '@roadworld/physics';
import type { RgbColor } from '@roadworld/shared';
import type { Surface } from '../audio/TireSound.js';
import type { InputState } from '../input/Input.js';
import type { SurfaceKind } from '../world/surface.js';
import { createChassisMesh, createWheelMesh } from './chassisFactory.js';
import type { IVehicle, SurfaceLookup, VehicleKind } from './IVehicle.js';
import { VehicleTuning, type SurfaceHandlingTable, type TreeFellingConfig } from './tuning.js';
import { VehicleFx, type CoverSampler } from './VehicleFx.js';

interface WheelState {
  steers: boolean;
  driven: boolean;
  visual: Mesh;
}

export class Car implements IVehicle {
  readonly kind: VehicleKind = 'car';
  private physics: PhysicsWorld;
  private body: RAPIER.RigidBody;
  private collider: RAPIER.Collider;
  private controller: RAPIER.DynamicRayCastVehicleController;
  private wheels: WheelState[] = [];
  private root: TransformNode;
  private bodyMesh: TransformNode;
  private chassisMeshes: Mesh[] = [];
  private currentSteer = 0;
  private brakeSmoothed = 0;
  private spawnPos: Vector3;
  private spawnYaw: number;
  private enabled = true;
  private fx: VehicleFx;
  private coverSampler: CoverSampler = () => null;
  private prevWheelRot: number[] = [0, 0, 0, 0];
  /** Most recent crash-impulse magnitude (horizontal dv), cleared by consumeImpactImpulse(). */
  private lastImpactDv = 0;
  /** Live multiplier on engine throttle force. Brakes are not scaled. */
  private powerMultiplier = 1.0;
  // --- Engine-audio / RPM state (feat/audio) ---
  /** Player's instantaneous engine command magnitude in [0, 1] — the larger of
   *  forward-throttle and reverse-pedal-when-actually-reversing. Real engines
   *  rev when you press the accelerator in any gear (reverse is a transmission
   *  flip, not an engine direction), so we treat reverse input as engine work. */
  private lastEngineInput = 0;
  private currentGear = 1;
  private smoothedRpm = 800;
  private gaugeRpm = 800;
  // --- Tree-felling state (master) ---
  /** Optional per-vehicle override of tree-felling thresholds. Future vehicles (logging
   *  truck, scooter) can pass their own; if null, falls back to VehicleTuning.treeFelling. */
  treeFelling: TreeFellingConfig | null = null;
  /** Pre-step velocity snapshot. Captured by beginFrame() before physics.step so post-step
   *  systems (tree felling) can reason about the velocity entering the step rather than the
   *  velocity Rapier produced — necessary because a solid tree collider can fully stop the
   *  car in a single substep, hiding the "I was going 70 km/h" intent. */
  private prevLinvelX = 0;
  private prevLinvelY = 0;
  private prevLinvelZ = 0;
  private prevHSpeed = 0;
  // --- Surface-aware handling (master) ---
  /** Set from the outside (e.g. via ChunkManager.classifySurface). Without it, every surface
   *  reads as 'road' and the multipliers are no-ops — safe default. */
  surfaceLookup: SurfaceLookup | null = null;
  /** Optional per-vehicle override of the surface-handling table (throttle + grip + drag).
   *  Future vehicles (dirt bike, sports car) pass their own; if null, falls back to
   *  VehicleTuning.surfaceHandling. */
  surfaceHandling: SurfaceHandlingTable | null = null;
  /** Last damping value pushed to Rapier — only re-set on change to avoid per-substep cost. */
  private appliedLinearDamping: number;
  private appliedAngularDamping: number;
  /** Last per-wheel friction values pushed; skip re-applying when unchanged. Tracked as
   *  effective (post-multiplier) numbers so HMR-tuned tables take effect on the next tick.
   *  Front and rear tracked separately because surface multipliers only affect rear wheels. */
  private appliedFrontSlip: number;
  private appliedFrontSide: number;
  private appliedRearSlip: number;
  private appliedRearSide: number;
  // --- Dynamic grip state (master) ---
  /** Dynamic grip state — see VehicleTuning.gripStress doc. Score = 1 → full rear grip;
   *  score = 0 → rear grip = surfaceGripFloor. Drains under combined stress, recovers
   *  when calm. Smoothed accelG/steerRate avoid single-substep spikes overpunishing. */
  private gripScore = 1.0;
  private smoothedAccelG = 0;
  private smoothedSteerRate = 0;
  private prevForwardSpeedForGrip = 0;
  private prevSteerForGrip = 0;

  constructor(
    physics: PhysicsWorld,
    scene: Scene,
    spawnPos: Vector3,
    spawnYaw = 0,
    chassisColor?: RgbColor,
  ) {
    this.physics = physics;
    this.spawnPos = spawnPos.clone();
    this.spawnYaw = spawnYaw;

    const T = VehicleTuning;

    // --- Rigid body + chassis collider ---
    const bodyDesc = RAPIER.RigidBodyDesc.dynamic()
      .setTranslation(spawnPos.x, spawnPos.y, spawnPos.z)
      .setRotation(yawToQuat(spawnYaw))
      .setCcdEnabled(true)
      .setLinearDamping(T.linearDamping)
      // Higher angular damping resists pitch/roll impulses from hard braking and
      // bumpy terrain without making steering feel sluggish.
      .setAngularDamping(T.angularDamping);
    this.body = physics.world.createRigidBody(bodyDesc);
    this.appliedLinearDamping = T.linearDamping;
    this.appliedAngularDamping = T.angularDamping;
    this.appliedFrontSlip = T.frictionSlip;
    this.appliedFrontSide = T.sideFrictionStiffness;
    this.appliedRearSlip = T.frictionSlip;
    this.appliedRearSide = T.sideFrictionStiffness;

    // Density=0 so the collider contributes no mass/inertia; we set those explicitly below
    // to put the center of mass below the visual chassis center.
    const colliderDesc = RAPIER.ColliderDesc.cuboid(
      T.chassisWidth * 0.5,
      T.chassisHeight * 0.5,
      T.chassisLength * 0.5,
    )
      .setDensity(0)
      .setFriction(0.4)
      .setRestitution(0.0)
      .setActiveEvents(RAPIER.ActiveEvents.COLLISION_EVENTS);
    this.collider = physics.world.createCollider(colliderDesc, this.body);

    // Explicit mass + low CoM + cuboid principal inertia. The "additional" combines with
    // the (now-zero) collider mass, so these values are authoritative.
    const w = T.chassisWidth;
    const h = T.chassisHeight;
    const d = T.chassisLength;
    const inertiaX = (T.chassisMass / 12) * (h * h + d * d); // pitch
    const inertiaY = (T.chassisMass / 12) * (w * w + d * d); // yaw
    const inertiaZ = (T.chassisMass / 12) * (w * w + h * h); // roll
    this.body.setAdditionalMassProperties(
      T.chassisMass,
      { x: 0, y: T.centerOfMassY, z: 0 },
      { x: inertiaX, y: inertiaY, z: inertiaZ },
      { x: 0, y: 0, z: 0, w: 1 },
      true,
    );

    // --- Vehicle controller + wheels ---
    this.controller = physics.world.createVehicleController(this.body);
    const suspensionDir = { x: 0, y: -1, z: 0 };
    const wheelAxle = { x: -1, y: 0, z: 0 };

    const corners: Array<{ x: number; z: number; steers: boolean; driven: boolean }> = [
      { x: T.wheelTrack, z: T.frontAxle, steers: true, driven: true },
      { x: -T.wheelTrack, z: T.frontAxle, steers: true, driven: true },
      { x: T.wheelTrack, z: -T.rearAxle, steers: false, driven: true },
      { x: -T.wheelTrack, z: -T.rearAxle, steers: false, driven: true },
    ];

    // --- Visuals ---
    this.root = new TransformNode('vehicle-root', scene);
    this.bodyMesh = createChassisMesh(scene, T, chassisColor);
    this.bodyMesh.parent = this.root;
    // Snapshot the body + cabin meshes so we can register them as shadow casters and
    // exclude them from motion blur. chassisFactory returns a TransformNode parent.
    for (const cm of this.bodyMesh.getChildMeshes()) {
      if (cm instanceof Mesh) this.chassisMeshes.push(cm);
    }

    for (let i = 0; i < corners.length; i++) {
      const c = corners[i]!;
      this.controller.addWheel(
        { x: c.x, y: T.wheelAnchorY, z: c.z },
        suspensionDir,
        wheelAxle,
        T.suspensionRestLength,
        T.wheelRadius,
      );
      this.controller.setWheelSuspensionStiffness(i, T.suspensionStiffness);
      this.controller.setWheelMaxSuspensionTravel(i, T.suspensionTravel);
      this.controller.setWheelMaxSuspensionForce(i, T.maxSuspensionForce);
      this.controller.setWheelSuspensionCompression(i, T.suspensionCompression);
      this.controller.setWheelSuspensionRelaxation(i, T.suspensionRelaxation);
      this.controller.setWheelFrictionSlip(i, T.frictionSlip);
      this.controller.setWheelSideFrictionStiffness(i, T.sideFrictionStiffness);

      const wheelVisual = createWheelMesh(scene, T, `wheel-${i}`);
      wheelVisual.parent = this.root;
      this.wheels.push({ steers: c.steers, driven: c.driven, visual: wheelVisual });
    }

    // Use the chassis body mesh as the FX anchor so particle systems can parent to it
    // (ParticleSystem.emitter requires an AbstractMesh, not a TransformNode).
    this.fx = new VehicleFx(
      scene,
      this.chassisMeshes[0]!,
      this.wheels.map((w) => w.visual),
    );
  }

  /** Hand the chunk cover-color resolver to the FX system for off-road dirt tinting. */
  setCoverSampler(sampler: CoverSampler): void {
    this.coverSampler = sampler;
  }

  /** Live engine-force multiplier. 1.0 = original tuning; 0.5 = half power & ~half top speed. */
  setPowerMultiplier(v: number): void {
    this.powerMultiplier = v;
  }

  /** Apply input and step the controller. Must be called each frame BEFORE physics.step. */
  applyInput(input: InputState, dt: number): void {
    const T = VehicleTuning;
    const linvel = this.body.linvel();
    const speed = Math.hypot(linvel.x, linvel.y, linvel.z);

    // What surface are we on? Affects throttle (top speed), body damping (coast feel), and
    // per-wheel grip (drift / slide on grass). Unknown surface (chunk still loading) defaults
    // to 'road' so the car doesn't lurch when crossing into an unfetched area.
    const pos = this.body.translation();
    const surfaceKind: SurfaceKind = this.surfaceLookup?.(pos.x, pos.z) ?? 'road';
    const surfaceTable = this.surfaceHandling ?? (T.surfaceHandling as SurfaceHandlingTable);
    const sCfg = surfaceTable[surfaceKind];
    const targetDamping = T.linearDamping + sCfg.extraLinearDamping;
    if (targetDamping !== this.appliedLinearDamping) {
      this.body.setLinearDamping(targetDamping);
      this.appliedLinearDamping = targetDamping;
    }
    // Angular damping is bumped on loose surfaces so the drift bleeds off quickly after
    // steering is released — gives a predictable ~1s "tail" rather than a multi-second spin.
    const targetAngDamping = T.angularDamping + sCfg.extraAngularDamping;
    if (targetAngDamping !== this.appliedAngularDamping) {
      this.body.setAngularDamping(targetAngDamping);
      this.appliedAngularDamping = targetAngDamping;
    }
    // Speed-sensitive steering envelope. Pulled up before the grip block so the grip
    // score's steer-rate factor sees this substep's currentSteer (not last substep's).
    const speedRatio = Math.min(1, speed / T.steerSpeedReference);
    const maxSteer = T.maxSteer + (T.minSteerAtHighSpeed - T.maxSteer) * speedRatio;
    const targetSteer = input.steer * maxSteer;
    const ramp = 1 - Math.exp(-T.steerRate * dt);
    this.currentSteer += (targetSteer - this.currentSteer) * ramp;

    // forwardSpeed pulled up too (was below) — grip block needs its derivative.
    const horizSpeed = Math.hypot(linvel.x, linvel.z);
    const forwardSpeed = this.getForwardSpeed();

    // --- Dynamic grip score ---
    // Three stress factors averaged, gated by a speed exponent, then scaled by surface.
    // Speed is a GATE rather than a peer: at low speeds the gate is small, so even max
    // lateral G / accel / steering rate barely registers. At high speeds the gate opens
    // and the other factors decide how much grip you lose. Result: casual off-road
    // driving at 80–100 km/h keeps grip, but a 300 km/h botched turn fully breaks loose.
    // gripScore drifts toward (1 - stress) at separate loss/recover rates so the player
    // can feel grip recover by easing off, which is what makes counter-steer effective.
    const G = T.gripStress;
    const smoothAlpha = 1 - Math.exp(-dt / G.derivativeSmoothing);
    const speedF = Math.min(1, speed / G.speedFull);
    const speedGate = Math.pow(speedF, G.speedExponent);
    const av = this.body.angvel();
    const lateralG = speed * Math.abs(av.y); // centripetal
    const lateralGF = Math.min(1, lateralG / G.lateralGFull);
    const rawAccelG = Math.abs(forwardSpeed - this.prevForwardSpeedForGrip) / Math.max(dt, 1e-4);
    this.smoothedAccelG += (rawAccelG - this.smoothedAccelG) * smoothAlpha;
    const accelGF = Math.min(1, this.smoothedAccelG / G.accelGFull);
    const rawSteerRate = Math.abs(this.currentSteer - this.prevSteerForGrip) / Math.max(dt, 1e-4);
    this.smoothedSteerRate += (rawSteerRate - this.smoothedSteerRate) * smoothAlpha;
    const steerRateF = Math.min(1, this.smoothedSteerRate / G.steerRateFull);

    const otherFactors = (lateralGF + accelGF + steerRateF) / 3;
    const rawStress = otherFactors * speedGate;
    const stress = Math.min(1, rawStress * sCfg.gripStressMul);
    const targetGrip = 1 - stress;
    const delta = targetGrip - this.gripScore;
    const stepRate = delta < 0 ? T.gripLossRate : T.gripRecoverRate;
    const maxStep = stepRate * dt;
    this.gripScore += delta < -maxStep ? -maxStep : delta > maxStep ? maxStep : delta;

    const effectiveRearMul =
      sCfg.surfaceGripFloor + (1 - sCfg.surfaceGripFloor) * this.gripScore;

    // Grip: only the REAR wheels lose grip on loose surfaces. Fronts keep base values so
    // steering input still bites and the car rotates around the front (oversteer / drift)
    // instead of plowing straight (understeer). Front cache short-circuits on no-change;
    // rear targets change every substep (gripScore is continuous) so push unconditionally.
    const targetFrontSlip = T.frictionSlip;
    const targetFrontSide = T.sideFrictionStiffness;
    const targetRearSlip = T.frictionSlip * effectiveRearMul;
    const targetRearSide = T.sideFrictionStiffness * effectiveRearMul;
    const frontChanged =
      targetFrontSlip !== this.appliedFrontSlip || targetFrontSide !== this.appliedFrontSide;
    for (let i = 0; i < this.wheels.length; i++) {
      if (this.wheels[i]!.steers) {
        if (frontChanged) {
          this.controller.setWheelFrictionSlip(i, targetFrontSlip);
          this.controller.setWheelSideFrictionStiffness(i, targetFrontSide);
        }
      } else {
        this.controller.setWheelFrictionSlip(i, targetRearSlip);
        this.controller.setWheelSideFrictionStiffness(i, targetRearSide);
      }
    }
    if (frontChanged) {
      this.appliedFrontSlip = targetFrontSlip;
      this.appliedFrontSide = targetFrontSide;
    }
    this.appliedRearSlip = targetRearSlip;
    this.appliedRearSide = targetRearSide;

    // Throttle = forward engine force on driven wheels. Brake (S key) is NOT applied via
    // Rapier's wheel brake — that creates a forward pitch torque proportional to the brake
    // force, which flipped the car at high speed even with lowered CoM and damping. Instead
    // we apply brake as a direct linear impulse at the body's center of mass: zero pitch
    // torque by definition. Handbrake stays on the rear wheels for arcade skid feel.

    // Blend brake (deceleration) vs reverse engine, based on forward speed. Hoisted above
    // the engine-input capture so audio/RPM can treat reverse-pedal as engine work too.
    const brakeWeight = clamp01(forwardSpeed / T.brakeReverseBlendSpeed);
    const reverseWeight = 1 - brakeWeight;

    // Engine-input capture: max of forward throttle and reverse-pedal-when-actually-reversing.
    // When braking at high forward speed, reverseWeight ≈ 0 so the brake input contributes
    // nothing (engine drops to idle — braking is not engine work). When stopped or moving
    // backward, reverseWeight ≈ 1 and the S key acts as a reverse accelerator, revving the
    // engine. Used by the RPM gauge AND the engine-sound ignition state machine.
    this.lastEngineInput = Math.max(input.throttle, input.brake * reverseWeight);

    // Apply the live power multiplier to throttle (and the reverse-engine portion of
    // the brake input). Brakes themselves are unscaled — slower car still stops normally.
    const scaledEngineForce = T.engineForce * this.powerMultiplier;

    let engineForce = 0;
    if (input.throttle > 0) engineForce = input.throttle * scaledEngineForce;

    // Smooth the brake intent toward the input. Same low-pass shape as steering.
    const brakeRamp = 1 - Math.exp(-T.brakeRampRate * dt);
    this.brakeSmoothed += (input.brake - this.brakeSmoothed) * brakeRamp;

    if (input.brake > 0) {
      engineForce += -input.brake * scaledEngineForce * 0.7 * reverseWeight;
    }

    // Apply brake as a CoM-centered impulse along the negative-velocity direction.
    // Only when moving with non-trivial speed (otherwise we'd jitter at standstill).
    if (this.brakeSmoothed > 0.01 && horizSpeed > 0.2) {
      const decelForce = this.brakeSmoothed * T.brakeForce * brakeWeight;
      const impulseMag = decelForce * dt; // F·dt = impulse
      const invSpeed = 1 / horizSpeed;
      const ix = -linvel.x * invSpeed * impulseMag;
      const iz = -linvel.z * invSpeed * impulseMag;
      this.body.applyImpulse({ x: ix, y: 0, z: iz }, true);
    }

    // Surface scales BOTH forward throttle and reverse engine — grass is grass either way.
    engineForce *= sCfg.throttleMul;

    const handbrake = input.handbrake ? T.handbrakeForce : 0;

    for (let i = 0; i < this.wheels.length; i++) {
      const w = this.wheels[i]!;
      this.controller.setWheelSteering(i, w.steers ? this.currentSteer : 0);
      this.controller.setWheelEngineForce(i, w.driven ? engineForce : 0);
      // Handbrake only — regular brake is handled above via CoM impulse.
      const rear = !w.steers;
      this.controller.setWheelBrake(i, rear ? handbrake : 0);
    }

    // Slip drag: opposing impulse along body-right axis, proportional to lateral velocity
    // squared. Quadratic so normal cornering (small lateral vel) is essentially free, but
    // a full sideways drift visibly bleeds speed — gives drift a real cost without nuking
    // arcade handling. Body-right vector = rotate (1,0,0) by body quaternion (xz part).
    const rot = this.body.rotation();
    const rightX = 1 - 2 * (rot.y * rot.y + rot.z * rot.z);
    const rightZ = 2 * (rot.x * rot.z - rot.y * rot.w);
    const linvelNow = this.body.linvel();
    const lateralVel = linvelNow.x * rightX + linvelNow.z * rightZ;
    if (lateralVel !== 0) {
      const sign = lateralVel < 0 ? -1 : 1;
      const dragMag =
        sign * lateralVel * lateralVel * T.slipDrag.coefficient * sCfg.slipDragMul * dt;
      this.body.applyImpulse({ x: -rightX * dragMag, y: 0, z: -rightZ * dragMag }, true);
    }

    // Yaw rate cap: even with the dynamic grip model, if the rear has fully broken loose
    // and the player holds the wheel, the front would otherwise spin the car up
    // indefinitely. Clamp |angvel.y| each substep — preserves roll/pitch components.
    const avCap = this.body.angvel();
    const cap = sCfg.maxYawRateRadS;
    if (avCap.y > cap) this.body.setAngvel({ x: avCap.x, y: cap, z: avCap.z }, true);
    else if (avCap.y < -cap) this.body.setAngvel({ x: avCap.x, y: -cap, z: avCap.z }, true);

    // Stash for next substep's grip derivatives. Done at the very end so any code above
    // that mutates currentSteer or velocity is reflected.
    this.prevForwardSpeedForGrip = forwardSpeed;
    this.prevSteerForGrip = this.currentSteer;

    this.controller.updateVehicle(dt);
  }

  /** Sync visual transforms from the simulated body. Call once per frame after physics.step. */
  syncVisual(): void {
    const t = this.body.translation();
    const q = this.body.rotation();
    this.root.position.set(t.x, t.y, t.z);
    this.ensureQuat(this.root).set(q.x, q.y, q.z, q.w);

    // Wheels: position in chassis-local frame from the controller.
    for (let i = 0; i < this.wheels.length; i++) {
      const w = this.wheels[i]!;
      const cs = this.controller.wheelChassisConnectionPointCs(i);
      if (!cs) continue;
      const suspensionLen = this.controller.wheelSuspensionLength(i) ?? VehicleTuning.suspensionRestLength;
      const steering = this.controller.wheelSteering(i) ?? 0;
      const rotation = this.controller.wheelRotation(i) ?? 0;

      w.visual.position.set(cs.x, cs.y - suspensionLen, cs.z);
      // Compose yaw (steering) * pitch (rolling rotation)
      const yaw = Quaternion.RotationAxis(new Vector3(0, 1, 0), steering);
      const pitch = Quaternion.RotationAxis(new Vector3(1, 0, 0), rotation);
      this.ensureQuat(w.visual).copyFrom(yaw.multiply(pitch));
    }
  }

  /**
   * Update particle effects. Call once per frame after `syncVisual`. Detects crashes
   * by watching for large frame-to-frame velocity changes (Rapier contact events would
   * be cleaner but more invasive to wire).
   */
  updateFx(dt: number): void {
    if (!this.enabled) return;
    const linvel = this.body.linvel();
    // Horizontal-plane velocity delta only — vertical bumps shouldn't register as crashes.
    const dvx = linvel.x - this.prevLinvelX;
    const dvz = linvel.z - this.prevLinvelZ;
    const dv = Math.hypot(dvx, dvz);
    this.prevLinvelX = linvel.x;
    this.prevLinvelY = linvel.y;
    this.prevLinvelZ = linvel.z;
    if (dv > 8) {
      this.fx.triggerCrash();
      if (dv > this.lastImpactDv) this.lastImpactDv = dv;
    }

    // Gather per-wheel grounded + spin + world position for the FX system.
    // Rapier exposes cumulative wheel rotation but not angular velocity, so we compute
    // the per-frame delta here.
    const positions: Vector3[] = [];
    const grounded: boolean[] = [];
    const spins: number[] = [];
    for (let i = 0; i < this.wheels.length; i++) {
      const w = this.wheels[i]!;
      w.visual.computeWorldMatrix(true);
      positions.push(w.visual.getAbsolutePosition().clone());
      grounded.push(this.controller.wheelIsInContact(i) ?? false);
      const rot = this.controller.wheelRotation(i) ?? 0;
      const prev = this.prevWheelRot[i] ?? rot;
      const omega = dt > 0 ? Math.abs(rot - prev) / dt : 0;
      this.prevWheelRot[i] = rot;
      spins.push(omega);
    }
    this.fx.update(dt, this.getForwardSpeed(), positions, grounded, spins, this.coverSampler);
  }

  position(): Vector3 {
    const t = this.body.translation();
    return new Vector3(t.x, t.y, t.z);
  }

  forward(): Vector3 {
    const q = this.body.rotation();
    const qq = new Quaternion(q.x, q.y, q.z, q.w);
    const f = new Vector3(0, 0, 1);
    f.rotateByQuaternionToRef(qq, f);
    return f;
  }

  speed(): number {
    const v = this.body.linvel();
    return Math.hypot(v.x, v.y, v.z);
  }

  getForwardSpeed(): number {
    const v = this.body.linvel();
    const f = this.forward();
    return v.x * f.x + v.y * f.y + v.z * f.z;
  }

  /**
   * Return (and clear) the latest crash-detected impulse magnitude. The camera reads this
   * each frame to trigger shake on hard impacts. Returns 0 if there hasn't been an impact
   * since the last call.
   */
  consumeImpactImpulse(): number {
    const v = this.lastImpactDv;
    this.lastImpactDv = 0;
    return v;
  }

  velocity(): Vector3 {
    const v = this.body.linvel();
    return new Vector3(v.x, v.y, v.z);
  }

  /** Rotation quaternion (xyzw) of the chassis. */
  rotationQuat(): { x: number; y: number; z: number; w: number } {
    const q = this.body.rotation();
    return { x: q.x, y: q.y, z: q.z, w: q.w };
  }

  /** Yaw in radians: 0 = facing +Z (north), increases clockwise toward +X (east). */
  getYaw(): number {
    const f = this.forward();
    return Math.atan2(f.x, f.z);
  }

  /** Current steering angle in radians (negative = left, positive = right). */
  steerAngle(): number {
    return this.currentSteer;
  }

  /** Update the chassis body color in place. */
  setChassisColor(color: RgbColor): void {
    const body = this.bodyMesh.getChildMeshes(false).find((m) => m.name === 'chassis-body');
    if (!body || !body.material) return;
    const mat = body.material as StandardMaterial;
    mat.diffuseColor = new Color3(color.r, color.g, color.b);
  }

  /** Chassis collider — exposed for systems that need contact queries against the vehicle. */
  getChassisCollider(): RAPIER.Collider {
    return this.collider;
  }

  /**
   * Snapshot linear velocity at the start of a frame. Must be called BEFORE physics.step()
   * so systems running after the step can reason about the velocity entering the step.
   */
  beginFrame(): void {
    const v = this.body.linvel();
    this.prevLinvelX = v.x;
    this.prevLinvelY = v.y;
    this.prevLinvelZ = v.z;
    this.prevHSpeed = Math.hypot(v.x, v.z);
  }

  /** Pre-step horizontal speed (m/s). Use this for "how fast was I going just before hitting
   *  the tree?" decisions; the post-step `speed()` may have been zeroed by the collision. */
  preStepHSpeedMs(): number {
    return this.prevHSpeed;
  }

  /** Pre-step horizontal direction as a unit vector (zero if stationary). */
  preStepHDirection(): { x: number; z: number } {
    if (this.prevHSpeed < 1e-3) return { x: 0, z: 0 };
    return { x: this.prevLinvelX / this.prevHSpeed, z: this.prevLinvelZ / this.prevHSpeed };
  }

  /**
   * Overwrite horizontal velocity to `(dirX, dirZ) * speedMs`, preserving current Y. Used
   * by the tree-felling system to restore momentum that Rapier's solid tree collider
   * erased in a single substep before we got a chance to remove the tree.
   */
  setHorizontalVelocityMs(dirX: number, dirZ: number, speedMs: number): void {
    const v = this.body.linvel();
    this.body.setLinvel({ x: dirX * speedMs, y: v.y, z: dirZ * speedMs }, true);
  }

  /**
   * Subtract `deltaMs` m/s from the car's horizontal speed, preserving direction. Used by
   * the tree-felling system to apply the piecewise-linear speed penalty per felled tree.
   * Clamps to zero (you never go backwards). Vertical velocity untouched.
   */
  applyImpactSpeedLossMs(deltaMs: number): void {
    if (deltaMs <= 0) return;
    const v = this.body.linvel();
    const hSpeed = Math.hypot(v.x, v.z);
    if (hSpeed < 1e-3) return;
    const newSpeed = Math.max(0, hSpeed - deltaMs);
    const scale = newSpeed / hSpeed;
    this.body.setLinvel({ x: v.x * scale, y: v.y, z: v.z * scale }, true);
  }

  /**
   * Effective tree-felling config — instance override if set, otherwise the global tuning.
   * Returning the resolved config so callers don't have to know about the fallback chain.
   */
  effectiveTreeFelling(): TreeFellingConfig {
    return this.treeFelling ?? (VehicleTuning.treeFelling as TreeFellingConfig);
  }

  // --- Audio integration (feat/audio) ---

  /** Rapier collider handle for the chassis. Used to filter collision events. */
  colliderHandle(): number {
    return this.collider.handle;
  }

  /**
   * What the car is currently driving on. Used to pick the tire-noise preset.
   *
   * Reads from the externally-injected `surfaceLookup` (typically wired up
   * to `ChunkManager.classifySurface` in main.ts). If no lookup is set or
   * the position is outside loaded chunks, defaults to 'road' — a sensible
   * fallback that keeps the audio quiet rather than picking grass/path by
   * accident.
   */
  getCurrentSurface(): Surface {
    if (!this.surfaceLookup) return 'road';
    const t = this.body.translation();
    return this.surfaceLookup(t.x, t.z) ?? 'road';
  }

  /**
   * Scan for the nearest sideways sound source (currently just walls/buildings
   * via raycast). Returns closeness, lateral bias for stereo panning, and the
   * radial approach velocity for Doppler.
   *
   * Returned shape:
   *   - proximity:        0..1 normalised closeness of the closer side
   *                       (0 = nothing in range, 1 = touching).
   *   - lateralBias:      -1..+1 — sign indicates which side dominates
   *                       (-1 = wall on left, +1 = wall on right). 0 when
   *                       both sides equal (e.g. tunnel) or no hits. Feeds
   *                       a StereoPannerNode so the whoosh comes from the
   *                       wall's side.
   *   - approachVelocity: m/s, signed. Positive when the car is moving
   *                       TOWARD the dominant side's hit point. Drives the
   *                       Doppler formula  f' = f · (v_sound + v_approach) / v_sound.
   *
   * Casts left and right independently so the lateral asymmetry is preserved.
   * Self-hits are filtered via Rapier's `filterExcludeCollider` arg.
   * `castRayAndGetNormal` lets us reject hits with a near-vertical normal
   * (terrain heightfield), keeping only vertical-wall surfaces — otherwise
   * sloped ground would constantly engage the whoosh on any hill.
   *
   * For traffic later: source velocity ≠ 0, so generalise to
   *   approach = (carVel − sourceVel) · toSourceDir
   */
  getNearestSideSource(maxDistance: number): {
    proximity: number;
    lateralBias: number;
    approachVelocity: number;
  } {
    const t = this.body.translation();
    const v = this.body.linvel();
    const fwd = this.forward();

    // World "right" in a left-handed Babylon scene (forward = +Z, up = +Y):
    //   right = up × forward = (forward.z, 0, -forward.x)
    let rx = fwd.z;
    let rz = -fwd.x;
    const rlen = Math.hypot(rx, rz);
    if (rlen < 1e-3) return { proximity: 0, lateralBias: 0, approachVelocity: 0 };
    rx /= rlen;
    rz /= rlen;

    const origin = { x: t.x, y: t.y + 0.3, z: t.z };

    const castSide = (dir: { x: number; y: number; z: number }) => {
      const ray = new RAPIER.Ray(origin, dir);
      const hit = this.physics.world.castRayAndGetNormal(
        ray,
        maxDistance,
        true,
        undefined,
        undefined,
        this.collider,
      );
      if (!hit) return { proximity: 0, approach: 0 };
      // Wall-only filter: vertical walls have |n.y| ≈ 0. Ground / hillside /
      // ceiling all have |n.y| close to 1. 0.5 threshold = surfaces tilted
      // more than ~30° from horizontal pass.
      if (Math.abs(hit.normal.y) > 0.5) return { proximity: 0, approach: 0 };
      return {
        proximity: 1 - hit.timeOfImpact / maxDistance,
        // Radial approach: velocity dotted with the unit ray direction.
        approach: v.x * dir.x + v.y * dir.y + v.z * dir.z,
      };
    };

    const right = castSide({ x: rx, y: 0, z: rz });
    const left = castSide({ x: -rx, y: 0, z: -rz });

    // Gain boost driver = the closer side's proximity.
    const proximity = Math.max(right.proximity, left.proximity);
    // Lateral bias for panning. Both sides equal (tunnel) → 0 (centred).
    const lateralBias = right.proximity - left.proximity;
    // Doppler from the dominant side. Tiebreaker = right (consistent).
    const approachVelocity =
      right.proximity >= left.proximity ? right.approach : left.approach;

    return { proximity, lateralBias, approachVelocity };
  }

  /**
   * Advance the RPM simulation by one frame. Updates both the wheel-driven
   * engine RPM (with simulated 5-speed gearbox) and the throttle-driven gauge
   * RPM. Call once per render frame BEFORE reading getEngineRpm()/getRpmGauge().
   *
   * Split into a separate tick so both values come from a single coherent
   * update — calling the getters multiple times in a frame doesn't double-step
   * the smoothing.
   */
  tickRpm(): void {
    const T = VehicleTuning;
    const gearRatios = [3.5, 2.2, 1.5, 1.1, 0.85];
    const finalDrive = 4.0;
    const idleRpm = 800;
    const redline = 7000;
    const shiftUp = 6200;
    const shiftDown = 2200;

    // --- Wheel-driven engine RPM with gearbox shifts ---
    const speed = Math.abs(this.getForwardSpeed());
    const wheelRpm = (speed / (2 * Math.PI * T.wheelRadius)) * 60;
    let targetRpm = wheelRpm * gearRatios[this.currentGear - 1]! * finalDrive;
    if (targetRpm > shiftUp && this.currentGear < gearRatios.length) {
      this.currentGear++;
      targetRpm = wheelRpm * gearRatios[this.currentGear - 1]! * finalDrive;
    } else if (targetRpm < shiftDown && this.currentGear > 1) {
      this.currentGear--;
      targetRpm = wheelRpm * gearRatios[this.currentGear - 1]! * finalDrive;
    }
    // Floor target with a "revving in place" offset proportional to engine input.
    // Uses lastEngineInput so reverse pedal also revs the engine at standstill.
    const throttleIdle = idleRpm + this.lastEngineInput * 2000;
    targetRpm = Math.max(throttleIdle, Math.min(redline, targetRpm));
    this.smoothedRpm += (targetRpm - this.smoothedRpm) * 0.15;

    // --- Gauge RPM — engine-input-driven, decays to idle when off ---
    // While ANY engine input is held (forward throttle or reverse pedal when
    // actually reversing), the gauge tracks the wheel-driven RPM (preserves
    // visible gear-shift drops). When released, decays to idle regardless of
    // motion: sliding down a hill keeps the needle low.
    if (this.lastEngineInput > 0.05) {
      this.gaugeRpm += (this.smoothedRpm - this.gaugeRpm) * 0.20;
    } else {
      this.gaugeRpm += (idleRpm - this.gaugeRpm) * 0.04;
    }
  }

  /**
   * Player's instantaneous engine-command magnitude in [0, 1]. Equal to the
   * forward throttle when accelerating, OR the reverse-pedal magnitude when
   * the brake input is being treated as reverse-engine force (i.e. car stopped
   * or moving backward). Used by the engine-sound ignition state machine so
   * reversing out of a parking spot produces audio.
   */
  getEngineInput(): number {
    return this.lastEngineInput;
  }

  /** Wheel-driven engine RPM with simulated gearbox. Pure getter — call tickRpm() to advance. */
  getEngineRpm(): number {
    return this.smoothedRpm;
  }

  /**
   * Throttle-driven gauge RPM. This is also what drives the engine audio:
   * idle pitch when off-throttle (so sliding down a hill sounds like idling,
   * not accelerating), revs up when throttle is applied (even when km/h is
   * dropping, e.g. braking backwards momentum). Pure getter — call tickRpm()
   * to advance.
   */
  getRpmGauge(): number {
    return this.gaugeRpm;
  }

  setSpawn(pos: Vector3, yaw = 0): void {
    this.spawnPos = pos.clone();
    this.spawnYaw = yaw;
  }

  respawn(): void {
    this.body.setTranslation({ x: this.spawnPos.x, y: this.spawnPos.y, z: this.spawnPos.z }, true);
    this.body.setRotation(yawToQuat(this.spawnYaw), true);
    this.body.setLinvel({ x: 0, y: 0, z: 0 }, true);
    this.body.setAngvel({ x: 0, y: 0, z: 0 }, true);
    this.fx.triggerStart();
  }

  /** Recover the car in place — lift to targetY, keep yaw, zero velocity. */
  recoverTo(x: number, y: number, z: number): void {
    this.body.setTranslation({ x, y, z }, true);
    this.body.setLinvel({ x: 0, y: 0, z: 0 }, true);
    this.body.setAngvel({ x: 0, y: 0, z: 0 }, true);
  }

  /** Hard-teleport to (pos, yaw) with zero velocities. Used by the swap path. */
  teleportTo(pos: Vector3, yaw: number): void {
    this.body.setTranslation({ x: pos.x, y: pos.y, z: pos.z }, true);
    this.body.setRotation(yawToQuat(yaw), true);
    this.body.setLinvel({ x: 0, y: 0, z: 0 }, true);
    this.body.setAngvel({ x: 0, y: 0, z: 0 }, true);
    // Reset transient handling state so the just-activated vehicle doesn't carry stale
    // grip / steering smoothing from before it was last disabled.
    this.currentSteer = 0;
    this.brakeSmoothed = 0;
    this.gripScore = 1.0;
    this.smoothedAccelG = 0;
    this.smoothedSteerRate = 0;
    this.prevForwardSpeedForGrip = 0;
    this.prevSteerForGrip = 0;
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  /**
   * Toggle the vehicle in/out of the simulation. While disabled the rigid body is removed
   * from Rapier's broad-phase (no integration, no collision) and the visual mesh is hidden.
   * Used to keep the car offscreen while the spawn chunk is loading.
   */
  setEnabled(enabled: boolean): void {
    if (this.enabled === enabled) return;
    this.enabled = enabled;
    this.body.setEnabled(enabled);
    if (enabled) {
      this.body.setLinvel({ x: 0, y: 0, z: 0 }, true);
      this.body.setAngvel({ x: 0, y: 0, z: 0 }, true);
      this.fx.triggerStart();
    }
    this.root.setEnabled(enabled);
  }

  dispose(): void {
    this.fx.dispose();
    this.root.dispose();
    // Order matters: the vehicle controller references the rigid body; remove the
    // controller first so the next physics.step() doesn't iterate an orphaned
    // controller pointing at a freed body (causes Rapier "recursive use" errors).
    this.physics.world.removeVehicleController(this.controller);
    this.physics.world.removeRigidBody(this.body);
  }

  /** Register all chassis + wheel meshes as shadow casters on the given generator. */
  registerShadowCasters(generator: CascadedShadowGenerator): void {
    for (const m of this.chassisMeshes) generator.addShadowCaster(m, true);
    for (const w of this.wheels) generator.addShadowCaster(w.visual, true);
  }

  /** Every visual mesh that makes up the car — chassis body + cabin + 4 wheels. */
  getVisualMeshes(): Mesh[] {
    return [...this.chassisMeshes, ...this.wheels.map((w) => w.visual)];
  }

  private ensureQuat(node: TransformNode): Quaternion {
    if (!node.rotationQuaternion) node.rotationQuaternion = Quaternion.Identity();
    return node.rotationQuaternion;
  }
}

function yawToQuat(yaw: number): RAPIER.Rotation {
  const half = yaw * 0.5;
  return { x: 0, y: Math.sin(half), z: 0, w: Math.cos(half) };
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}
